import { onMounted, onUnmounted, type Ref } from "vue";
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";

export function useVerticalDragScroll(targetEl: Ref<HTMLElement | null>, dragThreshold = 5) {
    const store = useTimelineMapStore();

    let isDragging = false;
    let isPotentialClick = false;
    let startY = 0;
    let startX = 0;
    let scrollStartY = 0;
    let velocity = 0;
    let lastY = 0;
    let raf = 0;

    let suppressNextClick = false;
    let removeClickCapture: (() => void) | null = null;

    function startMomentum() {
        cancelAnimationFrame(raf);
        function step() {
            window.scrollBy(0, -velocity);
            velocity *= 0.95;               // friction
            if (Math.abs(velocity) > 0.5) {
                raf = requestAnimationFrame(step);
            }
        }
        raf = requestAnimationFrame(step);
    }

    function cancelMomentum() {
        cancelAnimationFrame(raf);
    }

    function onPointerDown(e: PointerEvent) {
        cancelMomentum();
        isDragging = true;
        isPotentialClick = true;
        suppressNextClick = false;
        startY = e.clientY;
        startX = e.clientX;
        scrollStartY = window.scrollY;
        lastY = e.clientY;
        document.body.style.cursor = "grabbing";
        (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    }

    function onPointerMove(e: PointerEvent) {
        if (!isDragging) { return; }

        const moveY = e.clientY - startY;
        const moveX = e.clientX - startX;

        if (Math.abs(moveY) > dragThreshold || Math.abs(moveX) > dragThreshold) {
            isPotentialClick = false;
            suppressNextClick = true;
        }

        window.scrollTo({ top: scrollStartY - moveY, behavior: "auto" });
        velocity = e.clientY - lastY;
        lastY = e.clientY;
        store.isDragging = true;
    }

    function onPointerUp(_e: PointerEvent) {
        const wasClick = isPotentialClick;

        isDragging = false;
        isPotentialClick = false;
        document.body.style.cursor = "";
        store.isDragging = false;

        if (!wasClick && Math.abs(velocity) > 0.7) {
            startMomentum();
        }
    }

    onMounted(() => {
        const clickCapture = (ev: Event) => {
            const tl = targetEl.value ?? document.getElementById("timeline");
            if (!tl) return;
            if (!tl.contains(ev.target as Node)) return;

            if (suppressNextClick) {
                ev.preventDefault();
                ev.stopImmediatePropagation();
                suppressNextClick = false;
            }
        };

        document.addEventListener("click", clickCapture, { capture: true });
        removeClickCapture = () => {
            document.removeEventListener("click", clickCapture, { capture: true });
        };
    });

    onUnmounted(() => {
        cancelMomentum();
        removeClickCapture?.();
    });

    return { onPointerDown, onPointerMove, onPointerUp, cancelMomentum };
}
